import { useState } from 'react'
import { Shield, Plus, Edit, Trash2, Users } from 'lucide-react'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Checkbox } from '../components/ui/checkbox'
import { useRoles, useCreateRole, useUpdateRole, useDeleteRole, type Role } from '../hooks/useRoles'
import {
  PERMISSION_RESOURCES,
  PERMISSION_ACTIONS,
  RESOURCE_LABELS,
  ACTION_LABELS,
  isAdmin as isAdminPerms,
  type PermissionResource,
  type PermissionAction,
} from '../lib/permissions'

export function Roles() {
  const { data: rolesData, isLoading, error } = useRoles()
  const createRole = useCreateRole()
  const updateRole = useUpdateRole()
  const deleteRole = useDeleteRole()

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  const [deletingRole, setDeletingRole] = useState<Role | null>(null)

  // Form state
  const [formName, setFormName] = useState('')
  const [formPermissions, setFormPermissions] = useState<Record<string, string[]>>({})
  const [formError, setFormError] = useState('')

  const roles = rolesData?.data ?? []

  const openCreate = () => {
    setEditingRole(null)
    setFormName('')
    setFormPermissions({})
    setFormError('')
    setIsFormOpen(true)
  }

  const openEdit = (role: Role) => {
    setEditingRole(role)
    setFormName(role.name)
    // Parse permissions from backend format
    const perms: Record<string, string[]> = {}
    if (role.permissions && typeof role.permissions === 'object' && !role.permissions.all) {
      for (const [key, value] of Object.entries(role.permissions)) {
        if (Array.isArray(value)) {
          perms[key] = [...value]
        }
      }
    }
    setFormPermissions(perms)
    setFormError('')
    setIsFormOpen(true)
  }

  const openDelete = (role: Role) => {
    setDeletingRole(role)
    setIsDeleteOpen(true)
  }

  const togglePermission = (resource: PermissionResource, action: PermissionAction) => {
    setFormPermissions(prev => {
      const current = prev[resource] ?? []
      const has = current.includes(action)
      let next: string[]
      if (has) {
        next = current.filter(a => a !== action)
      } else {
        next = [...current, action]
      }
      const updated = { ...prev }
      if (next.length === 0) {
        delete updated[resource]
      } else {
        updated[resource] = next
      }
      return updated
    })
  }

  const handleSave = async () => {
    if (!formName.trim()) {
      setFormError('Введите название роли')
      return
    }
    setFormError('')

    try {
      if (editingRole) {
        await updateRole.mutateAsync({
          id: editingRole.id,
          data: { name: formName.trim(), permissions: formPermissions },
        })
      } else {
        await createRole.mutateAsync({
          name: formName.trim(),
          permissions: formPermissions,
        })
      }
      setIsFormOpen(false)
    } catch (err: any) {
      setFormError(err?.message || 'Ошибка сохранения')
    }
  }

  const handleDelete = async () => {
    if (!deletingRole) return
    try {
      await deleteRole.mutateAsync(deletingRole.id)
      setIsDeleteOpen(false)
      setDeletingRole(null)
    } catch (err: any) {
      setFormError(err?.message || 'Ошибка удаления')
    }
  }

  const getPermissionSummary = (role: Role): string => {
    if (!role.permissions || typeof role.permissions !== 'object') return 'Нет прав'
    if (isAdminPerms(role.permissions)) return 'Полный доступ'
    const resources = Object.keys(role.permissions).filter(k => k !== 'all')
    if (resources.length === 0) return 'Нет прав'
    return resources.map(r => RESOURCE_LABELS[r as PermissionResource] || r).join(', ')
  }

  if (isLoading) return <LoadingTable />
  if (error) return <ErrorState title="Ошибка загрузки ролей" description={String(error)} />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-7 h-7" />
            Управление ролями
          </h1>
          <p className="text-gray-500 mt-1">Настройка прав доступа для пользователей</p>
        </div>
        <Button onClick={openCreate} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Создать роль
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Роли ({roles.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Название</TableHead>
                <TableHead>Права доступа</TableHead>
                <TableHead className="w-[100px]">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map(role => (
                <TableRow key={role.id}>
                  <TableCell className="font-medium">{role.name}</TableCell>
                  <TableCell className="text-gray-500 text-sm max-w-md truncate">
                    {getPermissionSummary(role)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {!isAdminPerms(role.permissions) && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(role)}
                            title="Редактировать"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDelete(role)}
                            title="Удалить"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {roles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-gray-400 py-8">
                    Нет ролей
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingRole ? 'Редактирование роли' : 'Создание роли'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="role-name">Название роли</Label>
              <Input
                id="role-name"
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder="Например: Менеджер"
              />
            </div>

            <div className="space-y-3">
              <Label>Права доступа</Label>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-gray-700">Раздел</th>
                      {PERMISSION_ACTIONS.map(action => (
                        <th key={action} className="text-center px-4 py-2 font-medium text-gray-700 w-[120px]">
                          {ACTION_LABELS[action]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PERMISSION_RESOURCES.map(resource => (
                      <tr key={resource} className="border-t">
                        <td className="px-4 py-2 text-gray-800">{RESOURCE_LABELS[resource]}</td>
                        {PERMISSION_ACTIONS.map(action => (
                          <td key={action} className="text-center px-4 py-2">
                            <Checkbox
                              checked={(formPermissions[resource] ?? []).includes(action)}
                              onCheckedChange={() => togglePermission(resource, action)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {formError && (
              <p className="text-sm text-red-600">{formError}</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleSave}
              disabled={createRole.isPending || updateRole.isPending}
            >
              {createRole.isPending || updateRole.isPending ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удаление роли</DialogTitle>
          </DialogHeader>
          <p className="py-4 text-gray-600">
            Вы уверены, что хотите удалить роль <strong>{deletingRole?.name}</strong>?
            Это действие необратимо.
          </p>
          {formError && (
            <p className="text-sm text-red-600">{formError}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteRole.isPending}
            >
              {deleteRole.isPending ? 'Удаление...' : 'Удалить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
